'use strict';
// Traducción de nombres entre el documento de Mongo (en inglés, convención del proyecto) y el archivo de
// intercambio sustemia.energia.v2 (nombres fijados por Sustemia, que son los que usa el motor).
// Solo cambia NOMBRES y forma; los valores pasan tal cual (los códigos cerrados —«Productivo», «implementada»,
// «elec»…— son de Sustemia y quedan iguales en los dos lados).
//
// Cada tabla es una lista de [nombreMongo, nombreV2, subtabla?]. Un campo que no está en la tabla se informa en
// `desconocidos` (la prueba de ida y vuelta exige que no haya ninguno): así un campo nuevo no se pierde en silencio.

const LISTA = 'lista';
const OBJETO = 'objeto';
const MAPA = 'mapa'; // objeto con claves libres (id de requisito, id de pregunta…) y valores con subtabla

const T_MES = [
  ['month', 'month'],
  ['production', 'production'],
  ['energy', 'energy'],
  ['gridKwh', 'energia_red_kwh'],
  ['selfGenerationKwh', 'autogeneracion_kwh'],
  ['thermalKwh', 'energia_termica_kwh'],
  ['var2', 'var2'],
  ['event', 'evento'],
  ['exclude', 'excluir'],
];
const T_USE = [
  ['name', 'name'],
  ['process', 'proceso'],
  ['enpi', 'enpi'],
  ['share', 'share'],
  ['reliability', 'reliab'],
  ['variables', 'variables'],
  ['people', 'personas'],
  ['responsible', 'resp'],
];
const T_EQUIPO = [
  ['line', 'linea'],
  ['name', 'nombre'],
  ['process', 'proceso'],
  ['purpose', 'proposito'],
  ['power', 'potencia'],
  ['nameplate', 'placa'],
  ['source', 'fuente'],
  ['seu', 'seu'],
  ['use', 'uso'],
  ['kW', 'kW'],
  ['units', 'unidades'],
  ['hoursPerDay', 'hs_dia'],
  ['daysPerYear', 'dias_anio'],
  ['loadFactor', 'fc'],
  ['hours', 'hs'],
  ['measurements', 'mediciones'],
];
const T_OPERACION = [
  ['name', 'nombre'],
  ['category', 'cat'],
  ['source', 'fuente'],
  ['sourceType', 'tipoFuente'],
  ['consumption', 'consumo'],
  ['unit', 'unidad'],
  ['quantity', 'cantidad'],
  ['target', 'meta'],
  ['criterion', 'criterio'],
  ['elecFraction', 'fracElec'],
  ['exampleTarget', '_metaEjemplo'],
  ['exampleFraction', '_fracEjemplo'],
  ['exampleCriterion', '_criterioEjemplo'],
];
const T_MEDIDA = [
  ['order', 'orden'],
  ['name', 'name'],
  ['seu', 'seu'],
  ['baseOp', 'baseOp'],
  ['energySource', 'fuenteE'],
  ['type', 'tipo'],
  ['frac', 'frac'],
  ['fracNote', 'fracNota'],
  ['pct', 'pct'],
  ['pctMin', 'pctMin'],
  ['pctMax', 'pctMax'],
  ['pctSource', 'pctFuente'],
  ['pctMethod', 'pctMetodo'],
  ['investment', 'inv'],
  ['status', 'status'],
  ['responsible', 'resp'],
  ['date', 'fecha'],
  ['resources', 'recursos'],
  ['method', 'metodo'],
  ['verification', 'verificacion'],
  ['verifiedSavingsKwh', 'ahorro_verificado_kwh'],
  ['kwh', 'kwh'],
  ['usd', 'usd'],
  ['renewableKwh', 'kwhRenovable'],
  ['payback', 'payback'], // entrada solo en medidas manuales (sin baseOp); con baseOp el motor lo recalcula
  ['exampleVerification', '_verifEjemplo'],
];
const T_TARIFAS = [['elec', 'elec'], ['term', 'term'], ['note', 'nota']];
const T_COMBUSTIBLE = [['name', 'nombre'], ['rate', 'tarifa'], ['emissionFactor', 'factor'], ['backup', 'respaldo']];
const T_CONVERSION = [['fuel', 'combustible'], ['heatingValue', 'pci'], ['unit', 'unidad'], ['source', 'fuente']];
const T_PERIODO = [['from', 'desde'], ['to', 'hasta']];
const T_VERSION_LB = [
  ['version', 'version'],
  ['date', 'fecha'],
  ['userName', 'usuario'],
  ['type', 'tipo'],
  ['model', 'modelo'],
  ['previous', 'anterior'],
  ['from', 'desde'],
  ['to', 'hasta'],
  ['reason', 'motivo'],
];
const T_FACTOR_ESTATICO = [['factor', 'factor'], ['baselineValue', 'valor_lb'], ['currentValue', 'valor_actual'], ['date', 'fecha']];
const T_OBJETIVO = [
  ['objective', 'objetivo'],
  ['targetValue', 'meta_valor'],
  ['targetUnit', 'meta_unidad'],
  ['enpi', 'iden'],
  ['deadline', 'fecha_limite'],
  ['responsible', 'resp'],
  ['ecms', 'ecms'],
  ['isExample', '_ejemplo'], // lo usan los ejemplos MX y BR; falta en el schema v2 de Sustemia
];
const T_SUBREQ = [['answer', 'r'], ['evidence', 'ev']];
const T_IDEN = [['key', 'key'], ['operation', 'op'], ['use', 'uso'], ['frequency', 'frecuencia']];
const T_METRICAS = [['sam', 'sam'], ['kgPerGarment', 'kgPorPrenda']];
const T_GRAFICOS = [['A', 'A'], ['B', 'B']];
const T_DOCUMENTO = [['docType', 'tipo'], ['name', 'nombre'], ['date', 'fecha'], ['file', 'archivo']];
const T_CONCLUSION = [['t', 't'], ['x', 'x']];
const T_EVIDENCIA = [['name', 'nombre'], ['date', 'fecha'], ['file', 'archivo']];

const T_INSTALACION = [
  ['code', 'id'],
  ['name', 'name'],
  ['country', 'country'],
  ['sector', 'sector'],
  ['archetype', 'arquetipo'],
  ['productionUnit', 'unidadProd'],
  ['productionUnitShort', 'unidadProdCorta'],
  ['area', 'area'],
  ['employees', 'employees'],
  ['shifts', 'shifts'],
  ['scope', 'alcance'],
  ['scopeReview', 'alcanceRev'],
  ['lines', 'lines'],
  ['processes', 'processes'],
  ['femTypes', 'femTypes'],
  ['femNote', 'femNota'],
  ['sources', 'fuentes'], // en v2 cada fuente es una tupla [nombre, uso principal, costo]: ver FUENTES
  ['rates', 'tarifas', OBJETO, T_TARIFAS],
  ['emissionFactors', 'factores', OBJETO, T_TARIFAS],
  ['fuels', 'combustibles', LISTA, T_COMBUSTIBLE],
  ['conversions', 'conversiones', LISTA, T_CONVERSION],
  ['var2Name', 'var2Nombre'],
  ['var2Unit', 'var2Unidad'],
  ['baseline', 'lb', OBJETO, T_PERIODO],
  ['baselineModel', 'lbModelo'],
  ['baselineHistory', 'lbHist', LISTA, T_VERSION_LB],
  ['staticFactors', 'factoresEstaticos', LISTA, T_FACTOR_ESTATICO],
  ['objectives', 'objetivos', LISTA, T_OBJETIVO],
  ['detailRequirements', 'subreq', MAPA, T_SUBREQ],
  ['indices', 'idens', LISTA, T_IDEN],
  ['metricParams', 'metricParams', OBJETO, T_METRICAS],
  ['charts', 'graficos', OBJETO, T_GRAFICOS],
  ['reportCharts', 'informeGraficos'],
  ['documents', 'docs', LISTA, T_DOCUMENTO],
  ['docCheck', 'docCheck'],
  ['conclusions', 'conclusiones', LISTA, T_CONCLUSION],
  ['selfForm', '_selfForm'],
  ['selfEvid', '_selfEvid', MAPA, T_EVIDENCIA], // {idPregunta: [{nombre, fecha, archivo}]}
  ['selfNotes', '_selfNotes'],
  ['seus', 'seus', LISTA, T_USE],
  ['machinery', 'machinery', LISTA, T_EQUIPO],
  ['operations', 'operations', LISTA, T_OPERACION],
  ['ecms', 'ecms', LISTA, T_MEDIDA],
  ['monthly', 'consumos_mensuales', LISTA, T_MES],
  ['isExample', '_ejemplo'],
];

// Campos de Mongo que no son de la instalación (metadatos del sistema): no viajan en el archivo.
// practiceOf: copia para practicar de un ejemplo; readOnly: marca de la API para mostrar un ejemplo (no se guarda).
const META_MONGO = ['_id', '__v', 'createdAt', 'updatedAt', 'creator_user', 'company', 'site', 'active', 'user', 'practiceOf', 'readOnly'];
// Campos del archivo que el motor recalcula o que solo usa el prototipo: no se guardan (hoja 19 y schema v2).
const DERIVADOS_V2 = ['clauseImplementation', '_reg', '_reg2', '_audit', 'gen', '_casc', '_nota', 'editada'];
// Avisos que normalizarInstalacion() deja en la instalación (se recalculan al importar).
const AVISOS_V2 = /^_aviso/;

const esObjetoPlano = (v) => v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);
// Mongoose entrega Map en los campos tipo Map (salvo .lean() o flattenMaps): se tratan como objeto.
const aObjeto = (v) => (v instanceof Map ? Object.fromEntries(v) : v);
const clonarValor = (v) => {
  v = aObjeto(v);
  if (v == null || typeof v !== 'object') return v;
  if (typeof v.toHexString === 'function') return v.toHexString();
  return JSON.parse(JSON.stringify(v));
};

function convertir(obj, tabla, haciaV2, ruta, desconocidos) {
  obj = aObjeto(obj);
  if (!esObjetoPlano(obj)) return obj;
  const [desde, hasta] = haciaV2 ? [0, 1] : [1, 0];
  const porNombre = new Map(tabla.map((fila) => [fila[desde], fila]));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    const fila = porNombre.get(k);
    // id de fila que vino de Mongo (mongoAV2): vuelve como _id, así una fila editada en pantalla conserva su id
    if (!haciaV2 && k === 'id' && !fila && typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v)) { out._id = v; continue; }
    if (!fila) {
      if (haciaV2 ? META_MONGO.includes(k) : DERIVADOS_V2.includes(k) || AVISOS_V2.test(k) || k === 'id') continue;
      desconocidos.push(ruta + k);
      continue;
    }
    const destino = fila[hasta];
    const [, , tipo, sub] = fila;
    const val = aObjeto(v);
    if (tipo === LISTA && Array.isArray(val)) {
      out[destino] = val.map((x, i) => convertir(x, sub, haciaV2, `${ruta}${k}[${i}].`, desconocidos));
    } else if (tipo === OBJETO && esObjetoPlano(val)) {
      out[destino] = convertir(val, sub, haciaV2, `${ruta}${k}.`, desconocidos);
    } else if (tipo === MAPA && esObjetoPlano(val)) {
      out[destino] = Object.fromEntries(Object.entries(val).map(([id, x]) => [id,
        Array.isArray(x) ? x.map((y, i) => convertir(y, sub, haciaV2, `${ruta}${k}.${id}[${i}].`, desconocidos))
          : convertir(x, sub, haciaV2, `${ruta}${k}.${id}.`, desconocidos)]));
    } else {
      out[destino] = clonarValor(val);
    }
  }
  return out;
}

const FUENTES = ['name', 'mainUse', 'cost'];

// Documento de Mongo (o el objeto que recibe/manda la API) → archivo sustemia.energia.v2.
// Devuelve { archivo, desconocidos }.
function mongoAV2(doc) {
  const desconocidos = [];
  const d = aObjeto(doc && typeof doc.toObject === 'function' ? doc.toObject({ flattenMaps: true }) : doc) || {};
  const inst = convertir(d, T_INSTALACION, true, '', desconocidos);
  if (Array.isArray(d.sources)) inst.fuentes = d.sources.map((s) => FUENTES.map((k) => (s && s[k] != null ? String(s[k]) : '')));
  // Una conclusión guardada es siempre una conclusión editada por la planta (las automáticas no se guardan).
  if (Array.isArray(inst.conclusiones)) inst.conclusiones = inst.conclusiones.map((c) => ({ ...c, editada: true }));
  // Los id de las filas viajan como texto (uuid en el archivo, _id en Mongo).
  T_INSTALACION.filter((f) => f[2] === LISTA).forEach(([m, v]) => {
    (inst[v] || []).forEach((x, i) => { const id = d[m][i] && d[m][i]._id; if (id != null && esObjetoPlano(x)) x.id = String(id); });
  });
  return { archivo: { schema: 'sustemia.energia.v2', instalacion: inst }, desconocidos };
}

// Archivo sustemia.energia.v2 (o su `instalacion`) → campos del documento de Mongo.
// No incluye metadatos (site, company, creator_user…): los pone el servidor. Devuelve { doc, desconocidos }.
function v2AMongo(archivo) {
  const desconocidos = [];
  const inst = archivo && archivo.instalacion ? archivo.instalacion : archivo;
  const doc = convertir(inst, T_INSTALACION, false, '', desconocidos);
  if (Array.isArray(inst && inst.fuentes)) {
    doc.sources = inst.fuentes.map((t, i) => {
      if (!Array.isArray(t)) { desconocidos.push(`fuentes[${i}] (se esperaba [nombre, uso, costo])`); return {}; }
      if (t.length > FUENTES.length) desconocidos.push(`fuentes[${i}][${FUENTES.length}…]`);
      return Object.fromEntries(FUENTES.map((k, j) => [k, t[j] == null ? '' : String(t[j])]));
    });
  }
  return { doc, desconocidos };
}

module.exports = { mongoAV2, v2AMongo, TABLA_INSTALACION: T_INSTALACION };
